import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import type { Point } from '@os/engine';
import { useApi } from '../../app/providers';
import type { CommentMessage, CommentThread, Mentionable } from '../../lib/comment-types';
import { Button, IconButton, Popover, pointAnchor } from '../../ui';
import { escapeCloses, useEscapeAnywhere } from '../../ui/escape';
import { Icon } from '../../ui/icons';
import { CommentBody } from './CommentBody';
import { Composer } from './Composer';
import { useThreadMessages, type commentRights, type useCommentWrite } from './useComments';

type Props = {
  thread: CommentThread;
  at: Point;
  me: { id: string };
  rights: ReturnType<typeof commentRights>;
  mentionables: readonly Mentionable[];
  write: ReturnType<typeof useCommentWrite>;
  onClose: () => void;
};

/**
 * One thread next to its pin: messages, reply, resolve or reopen, edit and delete one's own messages. It closes by its
 * button, Escape from anywhere (not from a text surface) and a press outside; a pin toggles its own thread (P16-2).
 */
export function ThreadPopover({ thread, at, me, rights, mentionables, write, onClose }: Props) {
  const { t, i18n } = useTranslation();
  const api = useApi();
  const queryClient = useQueryClient();
  const messages = useThreadMessages(thread.id);
  const [editing, setEditing] = useState<string | null>(null);
  const popup = useRef<HTMLDivElement>(null);
  const close = useEscapeAnywhere(popup, onClose);
  // Against its pin, read again whenever the camera moves `at` (the point itself when the pin is not rendered).
  const anchor = useMemo(() => {
    const point = pointAnchor(at.x, at.y - 28);
    return { getBoundingClientRect: () => document.querySelector(`[data-pin="${CSS.escape(thread.id)}"]`)?.getBoundingClientRect() ?? point.getBoundingClientRect() };
  }, [at.x, at.y, thread.id]);

  useEffect(() => {
    // Opening a thread reads its notifications (the bell catches up on its next fetch).
    void api
      .markNotificationsRead(thread.id)
      .then((n) => {
        if (n > 0) void queryClient.invalidateQueries({ queryKey: ['notifications'] });
      })
      .catch(() => undefined);
  }, [api, queryClient, thread.id]);

  const list = messages.data ?? [];
  const names = new Map<string, string>([
    ...mentionables.map((m) => [m.userId, m.displayName] as const),
    ...list.flatMap((m) => (m.authorId && m.authorName ? [[m.authorId, m.authorName] as const] : [])),
  ]);
  const mine = thread.createdBy === me.id;
  const canResolve = rights.canResolveAny || (rights.canResolveOwn && mine);
  const unanswered = list.every((m) => m.deletedAt || m.authorId === me.id);
  const canDeleteThread = rights.canDeleteAny || (rights.canComment && mine && unanswered);
  const author = (m: CommentMessage) => m.authorName || (m.authorId ? '—' : t('comments.formerGuest'));
  const when = (iso: string) => new Date(iso).toLocaleString(i18n.language, { dateStyle: 'short', timeStyle: 'short' });

  return (
    <Popover.Root
      open
      onOpenChange={(open, details) => {
        if (open) return;
        if (details.reason === 'escape-key' && !escapeCloses(details.event as KeyboardEvent, popup.current)) return details.cancel();
        // A pin opens its thread or another one by its own click; leaving the popover with Tab keeps it open.
        if (details.reason === 'outside-press' && (details.event.target as Element | null)?.closest?.('[data-pin]')) return details.cancel();
        if (details.reason === 'focus-out') return details.cancel();
        close();
      }}
    >
      <Popover.Popup ref={popup} label={t('comments.thread')} anchor={anchor} side="right" align="start" initialFocus={popup} className="flex max-h-[70vh] w-80 flex-col gap-3 overflow-auto rounded-xl">
        <div className="flex items-center justify-between gap-2">
          {thread.resolvedAt ? <span className="font-sans text-xs font-medium text-fg-muted">{t('comments.resolved')}</span> : <span />}
          <div className="flex items-center gap-1">
            {canResolve && (
              <Button variant="ghost" size="sm" onClick={() => write.mutate({ kind: 'resolve', threadId: thread.id, resolved: !thread.resolvedAt })}>
                {thread.resolvedAt ? t('comments.reopen') : t('comments.resolve')}
              </Button>
            )}
            {canDeleteThread && (
              <Button variant="ghost" size="sm" onClick={() => write.mutate({ kind: 'deleteThread', threadId: thread.id }, { onSuccess: close })}>
                {t('comments.deleteThread')}
              </Button>
            )}
            <IconButton label={t('comments.close')} icon={<Icon icon={X} size={16} />} size="sm" onClick={close} />
          </div>
        </div>
        <ul className="flex flex-col gap-3">
          {list.map((m) => (
            <li key={m.id} className="flex flex-col gap-1">
              <div className="flex items-baseline justify-between gap-2">
                <span className="font-sans text-xs font-medium" style={{ color: m.authorColor ?? undefined }}>
                  {author(m)}
                </span>
                <span className="font-sans text-[11px] text-fg-muted">
                  {when(m.createdAt)}
                  {m.editedAt && !m.deletedAt ? ` · ${t('comments.edited')}` : ''}
                </span>
              </div>
              {m.deletedAt ? (
                <p className="font-sans text-sm italic text-fg-muted">{t('comments.deleted')}</p>
              ) : editing === m.id ? (
                <Composer
                  label={t('comments.edit')}
                  mentionables={mentionables}
                  initial={m.body}
                  submitLabel={t('comments.save')}
                  autoFocus
                  onCancel={() => setEditing(null)}
                  onSubmit={(body) => write.mutateAsync({ kind: 'edit', threadId: thread.id, id: m.id, body }).then(() => setEditing(null))}
                />
              ) : (
                <CommentBody body={m.body} names={names} />
              )}
              {m.authorId === me.id && !m.deletedAt && editing !== m.id && (
                <div className="flex gap-1">
                  {rights.canComment && (
                    <Button variant="ghost" size="sm" onClick={() => setEditing(m.id)}>
                      {t('comments.edit')}
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => write.mutate({ kind: 'delete', threadId: thread.id, id: m.id })}>
                    {t('comments.delete')}
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>
        {rights.canComment && (
          <Composer
            label={t('comments.replyPlaceholder')}
            mentionables={mentionables}
            onSubmit={(body) => write.mutateAsync({ kind: 'reply', threadId: thread.id, body })}
          />
        )}
        {write.isError && (
          <p role="alert" className="font-sans text-xs text-danger">
            {t('comments.error')}
          </p>
        )}
      </Popover.Popup>
    </Popover.Root>
  );
}
